import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { getUserPreferenceValue, selectUserId, useUpdateUserPreferencesMutation } from "fond/api";
import { Account, Store, UserPreferenceKey, UserPreferenceSubKey } from "fond/types";

type PinnedAccountItem = {
  account_id: string;
};

type UsePinnedReturn = {
  pinned: string[];
  addToPinned: (account: Account) => void;
  removeFromPinned: (account: Account) => void;
  setPinnedOrder: (orderedIds: string[]) => void;
  saving: boolean;
};

export const usePinned = (): UsePinnedReturn => {
  const userId = useSelector(selectUserId);
  const { enqueueSnackbar } = useSnackbar();
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();

  // Get pinned accounts from user preferences
  const pinnedFromPreferences = useSelector((state: Store) =>
    getUserPreferenceValue(state, UserPreferenceKey.ACCOUNT_NAVIGATION, UserPreferenceSubKey.PINNED_ACCOUNTS)
  );

  // Initialize state with current preference value if available
  const [pinned, setPinned] = useState<string[]>(() => {
    if (pinnedFromPreferences && Array.isArray(pinnedFromPreferences)) {
      return (pinnedFromPreferences as PinnedAccountItem[]).map((item) => item.account_id).filter(Boolean);
    }
    return [];
  });

  // Track saving state for API requests
  const [saving, setSaving] = useState(false);

  // Update local state when preferences change
  // API stores as [{ account_id: "uuid" }, ...] but we use string[] internally
  useEffect(() => {
    if (pinnedFromPreferences && Array.isArray(pinnedFromPreferences)) {
      const accountIds = (pinnedFromPreferences as PinnedAccountItem[]).map((item) => item.account_id).filter(Boolean);
      setPinned(accountIds);
    } else {
      setPinned([]);
    }
  }, [pinnedFromPreferences]);

  const removeFromPinned = useCallback(
    async (account: Account) => {
      if (!userId) return;

      // Attempt to remove from pinned only if it is in pinned
      if (pinned.includes(account.ID)) {
        const newPinnedItems = pinned.filter((item) => item !== account.ID);
        const apiPayload = newPinnedItems.map((id) => ({ account_id: id }));

        setSaving(true);
        try {
          await updateUserPreferences({
            Key: UserPreferenceKey.ACCOUNT_NAVIGATION,
            Subkey: UserPreferenceSubKey.PINNED_ACCOUNTS,
            Value: apiPayload,
          }).unwrap();

          enqueueSnackbar(`"${account.Name}" unpinned`);
          analytics.track("account.unpinned", {
            accountId: account.ID,
          });
        } catch (error) {
          enqueueSnackbar(`Failed to unpin "${account.Name}"`, { variant: "error" });
        } finally {
          setSaving(false);
        }
      }
    },
    [userId, pinned, updateUserPreferences, enqueueSnackbar]
  );

  const addToPinned = useCallback(
    async (account: Account) => {
      if (!userId) return;

      // Attempt to add to pinned only if it is not in pinned
      if (!pinned.includes(account.ID)) {
        const newPinnedItems = [...pinned, account.ID];
        const apiPayload = newPinnedItems.map((id) => ({ account_id: id }));

        setSaving(true);
        try {
          await updateUserPreferences({
            Key: UserPreferenceKey.ACCOUNT_NAVIGATION,
            Subkey: UserPreferenceSubKey.PINNED_ACCOUNTS,
            Value: apiPayload,
          }).unwrap();

          enqueueSnackbar(`"${account.Name}" pinned`);
          analytics.track("account.pinned", {
            accountId: account.ID,
          });
        } catch (error) {
          enqueueSnackbar(`Failed to pin "${account.Name}"`, { variant: "error" });
        } finally {
          setSaving(false);
        }
      }
    },
    [userId, pinned, updateUserPreferences, enqueueSnackbar]
  );

  const setPinnedOrder = useCallback(
    async (orderedIds: string[]) => {
      if (!userId) return;

      const apiPayload = orderedIds.map((id) => ({ account_id: id }));

      setSaving(true);
      try {
        await updateUserPreferences({
          Key: UserPreferenceKey.ACCOUNT_NAVIGATION,
          Subkey: UserPreferenceSubKey.PINNED_ACCOUNTS,
          Value: apiPayload,
        }).unwrap();
      } catch (error) {
        enqueueSnackbar("Failed to update pinned order", { variant: "error" });
      } finally {
        setSaving(false);
      }
    },
    [userId, updateUserPreferences, enqueueSnackbar]
  );

  return { pinned, addToPinned, removeFromPinned, setPinnedOrder, saving };
};
